import { useQuery, useMutation, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import { api } from '../api';
import { UserAPI } from '../../../../shared/types/api';
import { useAuth } from '../platform';

/**
 * Hook to fetch current user profile
 * @param options Optional React Query configuration
 *
 * The query is gated on `isSignedIn`, so it never fires while signed out.
 * Clearing user-scoped cache on sign-out is handled once, centrally, in
 * AuthProvider — doing it here would wipe the whole React Query cache (public
 * event lists included) on every mount while signed out, forcing redundant
 * refetches across the public /e pages.
 */
export function useProfile(options?: Omit<UseQueryOptions<UserAPI.GetProfileResponse>, 'queryKey' | 'queryFn'>) {
  const { isSignedIn } = useAuth();

  return useQuery({
    queryKey: ['profile'],
    queryFn: async (): Promise<UserAPI.GetProfileResponse> => {
      return api.get('/user/profile');
    },
    ...options,
    enabled: isSignedIn && (options?.enabled ?? true),
  });
}

export function useInvalidateProfile() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ['profile'] });
}

/**
 * Fetch another user's public profile (the safe "how others see you" subset).
 * Gated on `userId` so it only fires when a profile is actually requested.
 */
export function useUserPublicProfile(
  userId: string | null,
  options?: Omit<UseQueryOptions<UserAPI.GetPublicProfileResponse>, 'queryKey' | 'queryFn'>,
) {
  return useQuery({
    queryKey: ['publicProfile', userId],
    queryFn: async (): Promise<UserAPI.GetPublicProfileResponse> => {
      return api.get(`/user/${userId}/public`);
    },
    ...options,
    enabled: !!userId && (options?.enabled ?? true),
  });
}

/**
 * Whether the signed-in user has an active subscription entitlement.
 *
 * Returns false while signed out (the profile query is gated off then) and for
 * signed-in non-subscribers — this is the gate for member-only actions like
 * starting a Kiki.
 */
export function useIsSubscriber(): boolean {
  const { data } = useProfile();
  return (data?.data?.entitlements ?? []).some(
    (e) => e.type === 'subscription' && e.status === 'active',
  );
}

/**
 * Hook to update user profile (quiz answers)
 *
 * `silent` skips the refetch that normally follows a write. It exists for the
 * onboarding autosave, which fires while the user is still answering: the
 * invalidation turned every write into a second round trip, and the fresh
 * profile that came back re-rendered every consumer of `['profile']` — the
 * host page and the whole flow under it — about a second after a tap, for
 * data the client had just sent and already had. Anything that needs the
 * server's version of the profile afterwards should leave this off.
 */
export function useUpdateProfile(options?: { silent?: boolean }) {
  const queryClient = useQueryClient();
  const silent = options?.silent ?? false;

  return useMutation({
    mutationFn: async (data: UserAPI.UpdateProfileRequest): Promise<UserAPI.UpdateProfileResponse> => {
      return api.post('/user/profile', data);
    },
    onSuccess: () => {
      if (silent) return;
      // Invalidate profile query to refetch updated data
      queryClient.invalidateQueries({ queryKey: ['profile'] });
    },
  });
}

/**
 * Hook to merge an old Apple account into the current account
 */
export function useMergeAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (password: string): Promise<{ success: boolean; message?: string; error?: string }> => {
      return api.post('/user/merge-account', { password });
    },
    onSuccess: () => {
      // Invalidate profile query to refetch updated data after merge
      queryClient.invalidateQueries({ queryKey: ['profile'] });
    },
  });
}

